import {createRouter, createWebHistory} from 'vue-router'
import LangingView from '@/views/Langing.vue'
import AuthView from '@/views/Auth.vue'
import {useAuthStore} from '@/stores/auth'
import App from '@/App.vue'
import {guestGuard} from './guestGuard'
import {initReady} from '@/lib/init'
import {isL2User} from '@/composables/useL2Identity'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '',
      component: LangingView,
      beforeEnter: guestGuard,
    },
    {
      path: '',
      component: App,
      children: [
        {
          path: '/views',
          component: () => import('@/views/ViewList.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/views/create',
          component: () => import('@/views/ViewCreate.vue'),
          meta: {
            requiresAuth: true,
            permission: 'views|create',
          },
        },
        {
          path: '/views/:uuid',
          component: () => import('@/views/ViewUpdate.vue'),
          meta: {
            requiresAuth: true,
            permission: 'views|edit',
          },
        },
        {
          path: '/roles/create',
          component: () => import('@/views/RoleFormTree.vue'),
          meta: {requiresAuth: true, type: 'create'},
        },
        {
          path: '/roles/:uuid',
          component: () => import('@/views/RoleFormTree.vue'),
          meta: {requiresAuth: true, type: 'update'},
        },
        {
          path: '/entities/:entityUuid',
          component: () => import('@/views/Entities.vue'),
          meta: {requiresAuth: true},
        },
        {
          // Атрибуты ведутся в реестре app-go (configs/entities.yaml), мутации
          // создания и изменения на бэке удалены (NEDB-2569). Старые ссылки на
          // страницы создания и редактирования ведём к списку атрибутов.
          path: '/entities/:entityUuid/attribute/:uuid',
          redirect: to => `/entities/${to.params.entityUuid}`,
        },
        {
          path: '/catalogs/create',
          component: () => import('@/views/CatalogCreate.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/catalogs',
          component: () => import('@/views/CatalogList.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/catalogs/:uuid/create',
          component: () => import('@/views/CatalogValueCreate.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/catalogs/:uuid',
          component: () => import('@/views/CatalogUpdate.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/catalogs/:uuid/:catalogValueUuid',
          component: () => import('@/views/CatalogValueUpdate.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/aggregations',
          component: () => import('@/views/AggregationList.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/aggregations/create',
          component: () => import('@/views/AggregationForm.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/aggregations/:uuid',
          component: () => import('@/views/AggregationForm.vue'),
          props: true,
          meta: {requiresAuth: true},
        },
        {
          path: '/oqu-files',
          component: () => import('@/views/OquFilesList.vue'),
          meta: {
            requiresAuth: true,
            permission: 'oqu_files|read',
          },
        },
        {
          path: '/processes',
          component: () => import('@/views/Processes.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/processes/start/:type',
          name: 'process-start',
          component: () => import('@/views/Process.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/processes/run/:id',
          name: 'process-run',
          component: () => import('@/views/Process.vue'),
          meta: {requiresAuth: true},
        },
        {
          // Старый адрес /processes/:type?id=… — ссылки на него уже могли
          // разойтись: открытый процесс ведём на run, остальное — на запуск.
          path: '/processes/:type',
          redirect: to => (to.query.id
            ? {name: 'process-run', params: {id: String(to.query.id)}}
            : {name: 'process-start', params: {type: String(to.params.type)}, query: to.query}),
        },
        {
          path: '/l2/organizations',
          component: () => import('@/views/L2Organizations.vue'),
          meta: {requiresAuth: true, permission: 'l2|read'},
        },
        {
          path: '/l2/organizations/:uuid',
          component: () => import('@/views/L2OrganizationDetail.vue'),
          meta: {requiresAuth: true, permission: 'l2|read'},
        },
        {
          path: '/l2/organizations/:uuid/microdistrict/add',
          component: () => import('@/views/L2MicrodistrictAdd.vue'),
          meta: {requiresAuth: true, permission: 'l2|read'},
        },
        {
          path: '/l2/organizations/:uuid/applications/:appUuid',
          component: () => import('@/views/L2ApplicationEdit.vue'),
          meta: {requiresAuth: true, permission: 'l2|read'},
        },
        {
          path: '/l2/address-search',
          component: () => import('@/views/L2AddressSearch.vue'),
          meta: {requiresAuth: true, permission: 'l2|read'},
        },
        {
          path: '/l2/demands/:uuid/edit',
          component: () => import('@/views/L2DemandEdit.vue'),
          meta: {requiresAuth: true, permission: 'l2|read'},
        },
        {
          path: '/oqu-public-uploads/:uuid',
          component: () => import('@/views/OquFileDetail.vue'),
          meta: {
            requiresAuth: true,
            permission: 'oqu_files|read',
          },
        },
        {
          path: '/validation-tasks',
          component: () => import('@/views/ValidationTasks.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/:entity',
          component: () => import('@/views/EntityPreview.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/:entity/create',
          component: () => import('@/views/EntityPreview.vue'),
          meta: {requiresAuth: true, type: 'create'},
        },
        {
          path: '/:entity/:uuid/history',
          component: () => import('@/views/EntityHistory.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/:entity/:uuid',
          component: () => import('@/views/EntityPreview.vue'),
          meta: {requiresAuth: true, type: 'update'},
        },
        {
          path: '/no-access',
          component: () => import('@/views/NoAccess.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/debug/tree',
          component: () => import('@/views/DebugTreeView.vue'),
          meta: {requiresAuth: true},
        },
        {
          path: '/login',
          component: AuthView,
          beforeEnter: guestGuard,
        },
        {
          path: '/api/ORGC2-S-0405/redirect',
          component: () => import('@/views/DigitalIdRedirectHandler.vue'),
        },
        {
          path: '/api/aitu/redirect',
          component: () => import('@/views/AituRedirectHandler.vue'),
        },
        {
          path: '/api/:orgId/redirect',
          component: () => import('@/views/IdpRedirectHandler.vue'),
        },
      ],
    },
  ],
})

// Дефолтный маршрут для L2-юзера. Если он зайдёт на любой не-L2 маршрут,
// его редиректнет сюда. Меняется при добавлении L2-страниц.
const L2_DEFAULT_PATH = '/l2/organizations'

// Системные маршруты, доступные L2 кроме /l2/*
const L2_SYSTEM_PATHS = new Set(['/no-access', '/login'])

router.beforeResolve(async ({path, meta}) => {
  const authStore = useAuthStore()

  if (!meta.requiresAuth) {
    return true
  }

  if (!authStore.isAuth) {
    return path === '/login' ? true : {path: '/login'}
  }

  // Wait for init() to finish loading permissions before checking access
  if (!authStore.permissionsLoaded) {
    await initReady
  }

  // L2-юзер видит ТОЛЬКО /l2/* + системные маршруты. Любой переход на другой
  // путь редиректится на дефолтный L2-экран. Проверка по permission `l2|read`
  // (см. @/composables/useL2Identity). При миграции на динамический конфигуратор
  // этот блок удаляется.
  if (isL2User()) {
    const isL2Route = path.startsWith('/l2/')
    const isSystemRoute = L2_SYSTEM_PATHS.has(path)
    if (!isL2Route && !isSystemRoute) {
      return {path: L2_DEFAULT_PATH}
    }
    return true
  }

  if (path !== '/no-access' && authStore.userPermissions.size === 0) {
    return {path: '/no-access'}
  }

  return true
})

export default router
